import React,{useRef} from "react";
import { FormikProps } from "formik";
import { UIInput, UIReactSelect } from "components/Inputfields";


export type SelectOption = { value: string; label: string };
type FormikCompat<T = any> = FormikProps<T> & { initialStatus: any };
export type OfficeProductFormValues = {
  // add-only
  officeId: string;

  // shared
  name: string;
  sku: string;
  qty: number | string;
  threshold_qty: number | string;
  brand: string;
  category: string;
  item_condition: string;
  length: string;
  height: string;
  width: string;
  weight: string;
  description: string;
  statuss: string;

  // images (UI only)
  images: File[] | null;
};

type Props = {
  isEdit: boolean;
  formik: FormikCompat<OfficeProductFormValues>; // ✅ satisfies UIInput/UIReactSelect
  officeOptions: SelectOption[];
  brandOptions: SelectOption[];
  categoryOptions: SelectOption[];
  statusOptions: SelectOption[];
  conditionOptions: SelectOption[];
  // image previews (parent owns state)
  previewFiles: { file: File; url: string }[];
  uploadedFileMeta: { alt_text: string; url: string }[];
  onAddFiles: (files: File[]) => void;
  onRemovePreview: (index: number) => void;
  // Props (add this)
  onRemoveUploaded: (index: number) => void;
   resolveSrc?: (url: string) => string | null;

  // header + actions
  title: string;
  onBack: () => void;
  onSubmit: () => void;
  submitting: boolean;
   baseUrl?: string;
  /** When false, SKU is optional in the UI (default true). */
  requireSku?: boolean;
};

const INPUT_BG = "#f5f6f8";
const BORDER = "#e5e7eb";

const OfficeProductForm: React.FC<Props> = ({
  isEdit,
  formik,
  officeOptions,
  brandOptions,
  categoryOptions,
  statusOptions,
  conditionOptions,
  previewFiles,
  uploadedFileMeta,
  onAddFiles,
  onRemovePreview,
  onRemoveUploaded,
  title,
  onBack,
  onSubmit,
  submitting,
  resolveSrc,
   baseUrl,
  requireSku = true,
}) => {
    const fileInputRef = useRef<HTMLInputElement | null>(null);
  return (
    <div className="content container">
      {/* Page header */}
      <div className=" mb-4">
        <h2 className="m-0 ">{title}</h2>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }} noValidate>
        {/* Product Details card */}
        <div className="card border rounded-4 shadow-lg overflow-hidden mb-4">
          <div className="px-4 pt-0 pb-4">
            <div className="d-flex align-items-center mb-5" style={{ padding: "25px 18px", borderBottom: "1px solid #eef2f7" }}>
              <span className="d-inline-flex align-items-center justify-content-center" style={{ width: 26, height: 26 }}>
                <i className="bi bi-box-seam me-2 text-primary" style={{ fontSize: 26, color: "#155dfc", lineHeight: 1 }} />
              </span>
              <div className="fs-19 roboto-new ms-2">Product Details</div>
            </div>
            {/* <div className="d-flex align-items-center mb-2">
              <i className="bi bi-box-seam me-2 text-primary" />
              <div className="fw-semibold">Product Details</div>
            </div> */}

            <div className="row g-4">
              {/* LEFT: Basic Information */}
              <div className="col-lg-6">
                <div className="rounded-4 p-3">
                  <h5 className="fw-semibold text-dark border-bottom pb-2 mb-3">Basic Information</h5>

                  {!isEdit && (
                    <UIReactSelect
                      formik={formik as any}
                      name="officeId"
                      label="Office"
                      required
                      options={officeOptions}
                      placeholder="Select office"
                    />
                  )}

                  <div className="row g-3">
                    <div className="col-md-6">
                      <UIReactSelect
                        formik={formik as any}
                        name="brand"
                        label="Brand"
                        options={brandOptions}
                        placeholder="Select brand"
                      />
                    </div>
                    <div className="col-md-6">
                      <UIReactSelect
                        formik={formik as any}
                        name="category"
                        label="Category"
                        // required
                        options={categoryOptions}
                        placeholder="Select category"
                      />
                    </div>
                  </div>

                  {/* Images */}
                  <div className="mt-3">
                    <h5 className="fw-semibold text-dark border-bottom pb-2 mb-3">Image</h5>
                    <div
                      className="border-1 rounded-4 p-4 text-center"
                      style={{
                        borderStyle: "dashed",
                        background: "#fafafa",
                      }}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const files = Array.from(e.dataTransfer.files || []);
                        onAddFiles(files as File[]);
                      }}
                    >
                      <div className="mb-2">
                        <i className="bi bi-upload fs-2" />
                      </div>
                      <div className="mb-1">
                        <span className="fw-semibold">Choose files</span> or
                        drag and drop
                      </div>
                      <div className="text-muted small">
                        PNG, JPG up to 10MB each
                      </div>
                      <div className="text-muted small">
                        You can select multiple images at once
                      </div>
                      <div className="mt-3">
                        <label className="btn upload-btn mt-2">
                          Browse…
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            hidden
                            onChange={(e) => {
                              const files = Array.from(e.target.files || []);
                              onAddFiles(files as File[]);
                            }}
                          />
                        </label>
                      </div>
                    </div>

                    {/* Previews */}
                    {previewFiles.length > 0 && (
                      <div className="row g-3 mt-3">
                        {previewFiles.map((item, index) => (
                          <div key={`preview-${index}`} className="col-6 col-md-4 col-lg-3">
                            <div className="img-card position-relative">
                              <button
                                type="button"
                                className="btn-close img-x"
                                aria-label="Remove"
                                onClick={() => onRemovePreview(index)}
                              />
                              <img src={item.url} alt={`preview-${index}`} className="img-thumb" />
                            </div>

                            {/* filename under card, single line with … */}
                            <div className="small text-center mt-1 text-muted text-truncate" title={item.file.name}>
                              {item.file.name}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {uploadedFileMeta && uploadedFileMeta.length > 0 && (
                      <>
                        <div className="row g-3 mt-3">
                          <div className="col-12 mb-1">
                            <h6 className="text-muted">Already Uploaded Images</h6>
                          </div>
                              {(uploadedFileMeta ?? []).map((file, index) => {
                  const raw = file?.url;
                  const src =
                    // prefer a resolver if parent passed one; else fallback to baseUrl
                    (typeof resolveSrc === "function" ? resolveSrc(raw) : null) ||
                    (typeof raw === "string" && raw.startsWith("http")
                      ? raw
                      : `${baseUrl || process.env.REACT_APP_BASE_URL || ""}/${String(raw || "").replace(
                        /^https?:\/\/[^/]+\/(uploads\/.*)/,
                        "$1"
                      )}`);

                  return (
                    <div key={`saved-${index}`} className="col-6 col-md-4 col-lg-3 mb-3">
                      <div className="img-card">
                        <button
                          type="button"
                          className="btn-close img-x"
                          aria-label="Remove"
                          onClick={() => onRemoveUploaded(index)}     // ✅ index to parent
                        />
                        {src ? (
                          <img src={src} alt={file?.alt_text || "Image"} className="img-thumb" />
                        ) : (
                          <div className="text-muted small">Loading…</div>
                        )}
                      </div>
                    </div>
                  );
                })}
                          {/* {uploadedFileMeta.map((file, index) => (
                            <div key={`uploaded-${index}`} className="col-6 col-md-4 col-lg-3">
                              <div className="img-card position-relative">
                                <button
                                  type="button"
                                  className="btn-close img-x"
                                  aria-label="Remove"
                                  onClick={() => onRemoveUploaded(index)}
                                />
                                <img
                                  src={
                                    file.url?.startsWith("http")
                                      ? file.url
                                      : `${process.env.REACT_APP_BASE_URL}/${file.url?.replace(
                                        /^https?:\/\/[^\/]+\/(uploads\/.*)/,
                                        "$1"
                                      )}`
                                  }
                                  alt={file.alt_text}
                                  className="img-thumb"
                                />
                              </div>
                              <div className="small text-center mt-1 text-muted text-truncate">
                                {file.alt_text || `Image ${index + 1}`}
                              </div>
                            </div>
                          ))} */}
                        </div>
                      </>
                    )}


                    {/* New preview images */}
                    {previewFiles.length === 0 && uploadedFileMeta.length === 0 && (
                      <div className="col-12 text-center text-muted">
                        <i className="bi bi-image fs-2 d-block mb-2" />
                        No images uploaded yet
                      </div>
                    )}

                  </div>
                </div>
              </div>

              {/* RIGHT: Product Details */}
              <div className="col-lg-6">
                <div className="rounded-4 p-3">
                  <h5 className="fw-semibold text-dark border-bottom pb-2 mb-3">Product Details</h5>

                  <UIInput
                    formik={formik as any}
                    name="name"
                    label="Product Name"
                    required
                    placeholder="Enter product name"
                  />

                  <div className="row g-3">
                    <div className="col-md-8">
                      <UIInput
                        formik={formik as any}
                        name="sku"
                        label="SKU"
                        required={requireSku}
                        placeholder="Enter SKU"
                      />
                    </div>

                  </div>

                  <div className="row g-3">
                    <div className="col-md-6">
                      <UIInput
                        formik={formik as any}
                        name="qty"
                        label="Quantity"
                        required
                        placeholder="0"
                      />
                    </div>
                    <div className="col-md-6">
                      <UIInput
                        formik={formik as any}
                        name="threshold_qty"
                        label="Threshold Quantity"
                        placeholder="0"
                      />
                    </div>

                  </div>

                  <div className="mt-3">
                    <h5 className="fw-semibold text-dark border-bottom pb-2 mb-3">
                      Dimensions/Measurements (inches)
                    </h5>
                    <div className="row g-3">
                      <div className="col-md-4">
                        <UIInput formik={formik as any} name="length" label="Length" placeholder="0.0" />
                      </div>
                      <div className="col-md-4">
                        <UIInput formik={formik as any} name="height" label="Height" placeholder="0.0" />
                      </div>
                      <div className="col-md-4">
                        <UIInput formik={formik as any} name="width" label="Width" placeholder="0.0" />
                      </div>
                      <div className="col-md-12">
                        <UIInput formik={formik as any} name="weight" label="Weight (gms)" placeholder="0.0" />
                      </div>
                    </div>
                  </div>

                  <div className="row g-3 mt-1">
                    <div className="col-md-6">
                      <UIReactSelect
                        formik={formik as any}
                        name="item_condition"
                        label="Item Condition"
                        options={conditionOptions}
                        placeholder="Select condition"
                      />
                    </div>
                    <div className="col-md-6">
                      <UIReactSelect
                        formik={formik as any}
                        name="statuss"
                        label="Status"
                        options={statusOptions}
                        placeholder="Select status"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="row g-4 mt-4">
              <div className="col-lg-6">
                <div className="rounded-4 p-3">
                  <h5 className="fw-semibold text-dark border-bottom pb-2 mb-3">Product Description</h5>
                  <div className="fw-semibold mb-2">Description</div>
                  <textarea
                    id="description"
                    name="description"
                    className="form-control product-description-textarea"
                    rows={4}
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    style={{
                      borderRadius: 10,
                      background: INPUT_BG,
                      borderColor: BORDER,
                      fontSize: 14,
                    }}
                    placeholder="Enter shipping description (optional)"
                  />
                </div>
              </div>
            </div>

            <div className="row g-4">
              <div className="col-lg-12">
                <div className="rounded-4 p-3">
                  <div className="col-12 mb-2" id="row-overlap">
                    <hr className="ms-4 me-3" />
                  </div>

                  {/* footer */}
                  <div className="d-flex justify-content-between align-items-center ms-1" style={{ padding: "14px 18px" }}>
                    <button
                      type="button"
                      onClick={onBack}
                      className="btn back-btn surface-btn fw-bold fs-16"
                    >
                      <i className="bi bi-chevron-left me-2" />
                      Back
                    </button>

                    <button
                      type="submit"
                      className="btn btn-primary save-btn surface-btn fw-bold fs-16"
                      disabled={submitting}
                    >
                      {submitting ? (isEdit ? "Saving..." : "Saving...") : "Save"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <style>{`
        .rc-tabs{display:flex;gap:8px;flex-wrap:wrap}
        .rc-tab{border:1px solid #e5e7eb;background:#f8f9fb;padding:8px 14px;border-radius:10px;font-weight:600;color:#374151}
        .rc-tab:hover{background:#eef2ff}

        .imageuploadui{border:2px dashed #d1d5db;border-radius:14px;padding:40px 12px;text-align:center;cursor:pointer;transition:all .2s}
        .imageuploadui:hover{border-color:#6d28d9;background:#fbfaff}
        .uploadedimages{border-radius:10px}
          /* Thumbnail card so all X icons share the same anchor */
.img-card{
  position: relative;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  height: 180px;                 /* same height for all tiles */
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Image fits nicely without changing card height */
.img-thumb{
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 10px;
}

/* Close button perfectly aligned inside the tile */
.img-x{
  position: absolute;
  top: 0px;
  right: 0px;
  z-index: 2;
  opacity: 0.9;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: #fff;
  box-shadow: 0 1px 3px rgba(0,0,0,.15);
  padding: 0;
}

/* optional: hover feedback */
.img-card:hover .img-x{ opacity: 1; }

        /* show/hide desktop vs mobile */
        .rdt-desktop{display:block}
        .rdt-mobile{display:none}
        @media (max-width:1021px){
          .rdt-desktop{display:none}
          .rdt-mobile{display:block}
        }
      `}</style>
        </div>
      </form>
    </div>
  );
};

export default OfficeProductForm;
